// BLOKAS 1 Strategy.tsx: IMPORT STATEMENTS
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Bot, LineChart, CreditCard as Edit, Info, AlertCircle, AlertTriangle, Check, ChevronRight, TrendingUp, Timer, ArrowUpDown, CandlestickChart, LineChart as LineChartIcon, BarChart, Zap } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Tooltip } from '../../ui/Tooltip';
import { toast } from 'sonner';
import { buildPatchFromChange } from '../../utils/patchMap';
import { useAuth } from '../../contexts/AuthContext';
import { toStrategyKey } from '@/lib/strategyKeys';
import { generateIndicatorsForStrategy } from '@/lib/indicators';

console.log(`🔧 [BLOKAS 1 Strategy.tsx] Import statements loaded successfully`);

// BLOKAS 2 Strategy.tsx: STRATEGY INDICATORS DEFINITION
const strategyIndicators = {
  'trend': ['Moving Averages', 'MACD', 'ADX', 'Momentum Oscillators'],
  'scalping': ['Price Action', 'Level 2 Data', 'Volume', 'RSI'],
  'support_resistance': ['Price Action', 'Volume', 'Fibonacci Levels', 'Pivot Points'],
  'candle_patterns': ['Candlestick Patterns', 'Volume', 'Support/Resistance', 'Trend Lines'],
  'swing': ['Moving Averages', 'RSI', 'Fibonacci Retracements', 'Volume'],
  'chart_patterns': ['Volume', 'Trend Lines', 'Moving Averages', 'Momentum Indicators'],
  'fvg': ['Volume Profile', 'Order Flow', 'Market Profile', 'Time and Sales']
};

console.log(`🔧 [BLOKAS 2 Strategy.tsx] Strategy indicators definition loaded: ${Object.keys(strategyIndicators).length} strategies`);

// BLOKAS 3 Strategy.tsx: INTERFACE DEFINITIONS
interface StrategyProps {
  botId?: string;
  strategy: string;
  signalOnlyMode?: boolean;
  setStrategy: (strategy: string) => void;
  onSignalOnlyChange?: (value: boolean) => void;
  onIndicatorsChange?: (indicators: any[]) => void;
  initialIndicators?: any[];
}

interface StrategyOption {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  complexity: 'beginner' | 'intermediate' | 'advanced';
  tooltip: {
    description: string;
    pros: string[];
    cons: string[];
    bestFor: string[];
    indicators: string[];
  };
}

console.log(`🔧 [BLOKAS 3 Strategy.tsx] Interface definitions loaded: StrategyProps, StrategyOption`);

// BLOKAS 4 Strategy.tsx: STRATEGIES ARRAY DEFINITION
const strategies: StrategyOption[] = [
  {
    id: 'trend',
    name: 'Trend Following',
    description: 'Follow market trends using moving averages and momentum indicators',
    icon: <TrendingUp className="h-6 w-6" />,
    complexity: 'intermediate',
    tooltip: {
      description: 'A strategy that aims to capture gains by riding strong market trends and maintaining positions as long as the trend continues.',
      pros: [
        'Works well in strong trending markets',
        'Can capture large price movements',
        'Clear entry and exit rules'
      ],
      cons: [
        'Underperforms in sideways markets',
        'Can be late to enter and exit trends',
        'Multiple false signals in volatile markets'
      ],
      bestFor: [
        'Long-term traders',
        'Traders who can handle drawdowns',
        'Markets with clear trends'
      ],
      indicators: [
        'Moving Averages',
        'MACD',
        'ADX',
        'Momentum Oscillators'
      ]
    }
  },
  {
    id: 'scalping',
    name: 'Scalping',
    description: 'Make quick trades to profit from small price movements',
    icon: <Timer className="h-6 w-6" />,
    complexity: 'advanced',
    tooltip: {
      description: 'A high-frequency trading strategy that aims to profit from small price changes, making many trades with small profit targets.',
      pros: [
        'Frequent trading opportunities',
        'Small risk per trade',
        'Not affected by long-term market trends'
      ],
      cons: [
        'High trading fees',
        'Requires constant monitoring',
        'Stressful and time-intensive'
      ],
      bestFor: [
        'Active day traders',
        'Quick decision makers',
        'Traders with low-fee accounts'
      ],
      indicators: [
        'Price Action',
        'Level 2 Data',
        'Volume',
        'RSI'
      ]
    }
  },
  {
    id: 'support_resistance',
    name: 'Support & Resistance',
    description: 'Trade bounces off key support and resistance levels',
    icon: <ArrowUpDown className="h-6 w-6" />,
    complexity: 'beginner',
    tooltip: {
      description: 'A technical strategy that trades bounces off historically significant price levels where buying or selling pressure has previously halted price movements.',
      pros: [
        'Easy to understand and implement',
        'Clear entry and exit points',
        'Works in ranging markets'
      ],
      cons: [
        'Levels can break unexpectedly',
        'Subjective level identification',
        'Less effective in strong trends'
      ],
      bestFor: [
        'Beginning traders',
        'Range-bound markets',
        'Patient traders'
      ],
      indicators: [
        'Price Action',
        'Volume',
        'Fibonacci Levels',
        'Pivot Points'
      ]
    }
  },
  {
    id: 'candle_patterns',
    name: 'Candle Patterns',
    description: 'Trade based on Japanese candlestick patterns',
    icon: <CandlestickChart className="h-6 w-6" />,
    complexity: 'intermediate',
    tooltip: {
      description: 'Uses Japanese candlestick formations to identify potential market reversals and continuations based on historical price action patterns.',
      pros: [
        'Visual and easy to spot',
        'Works in all timeframes',
        'Strong psychological basis'
      ],
      cons: [
        'Requires pattern recognition skills',
        'Can generate false signals',
        'Needs confirmation from other indicators'
      ],
      bestFor: [
        'Visual traders',
        'Short to medium-term trading',
        'Multiple timeframe analysis'
      ],
      indicators: [
        'Candlestick Patterns',
        'Volume',
        'Support/Resistance',
        'Trend Lines'
      ]
    }
  },
  {
    id: 'swing',
    name: 'Swing Trading',
    description: 'Capture gains from short to medium-term price movements',
    icon: <LineChart className="h-6 w-6" />,
    complexity: 'intermediate',
    tooltip: {
      description: 'Aims to profit from price swings that occur over several days or weeks, focusing on capturing the bulk of market moves.',
      pros: [
        'Less time-intensive than day trading',
        'Can capture significant moves',
        'Lower trading costs'
      ],
      cons: [
        'Overnight and weekend risk',
        'Requires larger stop losses',
        'Can miss short-term opportunities'
      ],
      bestFor: [
        'Part-time traders',
        'Traders with day jobs',
        'Medium-term position holders'
      ],
      indicators: [
        'Moving Averages',
        'RSI',
        'Fibonacci Retracements',
        'Volume'
      ]
    }
  },
  {
    id: 'chart_patterns',
    name: 'Chart Patterns',
    description: 'Trade based on technical chart patterns',
    icon: <BarChart className="h-6 w-6" />,
    complexity: 'advanced',
    tooltip: {
      description: 'Identifies and trades classical chart patterns like head and shoulders, triangles, and flags that indicate potential trend reversals or continuations.',
      pros: [
        'Well-documented patterns',
        'Clear risk/reward ratios',
        'Works across all timeframes'
      ],
      cons: [
        'Patterns can fail',
        'Requires experience to identify',
        'Subjective pattern recognition'
      ],
      bestFor: [
        'Technical analysts',
        'Pattern recognition experts',
        'Patient traders'
      ],
      indicators: [
        'Volume',
        'Trend Lines',
        'Moving Averages',
        'Momentum Indicators'
      ]
    }
  },
  {
    id: 'fvg',
    name: 'Fair Value Gap (FVG)',
    description: 'Trade imbalances between price and fair value',
    icon: <Zap className="h-6 w-6" />,
    complexity: 'advanced',
    tooltip: {
      description: 'Identifies and trades price gaps where market inefficiencies create opportunities for price to return to its fair value.',
      pros: [
        'Based on market inefficiencies',
        'High probability setups',
        'Clear entry points'
      ],
      cons: [
        'Complex concept to master',
        'Gaps may not fill',
        'Requires multiple timeframe analysis'
      ],
      bestFor: [
        'Advanced traders',
        'Market microstructure experts',
        'Order flow traders'
      ],
      indicators: [
        'Volume Profile',
        'Order Flow',
        'Market Profile',
        'Time and Sales'
      ]
    }
  }
];

console.log(`🔧 [BLOKAS 4 Strategy.tsx] Strategies array loaded: ${strategies.length} strategies defined`);

// BLOKAS 5 Strategy.tsx: MAIN COMPONENT FUNCTION AND STATE VARIABLES
export const Strategy: React.FC<StrategyProps> = ({
  botId,
  strategy,
  signalOnlyMode: signalOnlyModeProp,
  setStrategy,
  onSignalOnlyChange,
  onIndicatorsChange,
  initialIndicators
}) => {
  console.log(`🔧 [BLOKAS 5 Strategy.tsx] Main component function initialized with props:`, {
    botId,
    strategy,
    signalOnlyModeProp,
    hasOnSignalOnlyChange: !!onSignalOnlyChange,
    hasOnIndicatorsChange: !!onIndicatorsChange,
    initialIndicatorsLength: initialIndicators?.length || 0
  });

  const { user } = useAuth();
  // SUB-BLOKAS 5.1 Strategy.tsx: STATE VARIABLES INITIALIZATION
  const [signalOnlyMode, setSignalOnlyMode] = useState(signalOnlyModeProp ?? false);
  const [lastStrategyKey, setLastStrategyKey] = useState<string | null>(strategy || null);
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(strategy || null);
  const prevStrategyIndicatorsRef = useRef<any[] | null>(null);

  console.log(`🔧 [SUB-BLOKAS 5.1 Strategy.tsx] State variables initialized:`, {
    signalOnlyMode,
    lastStrategyKey,
    selectedStrategy,
    hasPrevIndicators: !!prevStrategyIndicatorsRef.current
  });

  // BLOKAS 6 Strategy.tsx: USE EFFECT HOOKS
  // SUB-BLOKAS 6.1 Strategy.tsx: SYNC STRATEGY FROM PROPS EFFECT
  useEffect(() => {
    console.log(`🔧 [SUB-BLOKAS 6.1 Strategy.tsx] Hydration effect: syncing selectedStrategy with props.strategy`);
    if (strategy !== undefined && strategy !== null) {
      setSelectedStrategy(strategy);
      setLastStrategyKey(strategy);
      console.log(`🔧 [SUB-BLOKAS 6.1 Strategy.tsx] Strategy synced: ${strategy}`);
    }
  }, [strategy]);

  // SUB-BLOKAS 6.2 Strategy.tsx: SYNC SIGNAL ONLY MODE EFFECT
  useEffect(() => {
    console.log(`🔧 [SUB-BLOKAS 6.2 Strategy.tsx] Syncing signalOnlyMode from props`);
    if (signalOnlyModeProp !== undefined) {
      setSignalOnlyMode(signalOnlyModeProp);
      console.log(`🔧 [SUB-BLOKAS 6.2 Strategy.tsx] SignalOnlyMode synced: ${signalOnlyModeProp}`);
    }
  }, [signalOnlyModeProp]);

  // SUB-BLOKAS 6.3 Strategy.tsx: INITIAL INDICATORS CHECK EFFECT
  useEffect(() => {
    console.log(`🔧 [SUB-BLOKAS 6.3 Strategy.tsx] Checking initial indicators for Edit mode`);
    if (initialIndicators && initialIndicators.length > 0) {
      console.log('📊 Strategy: Using initial indicators from Edit mode');
      return;
    }
  }, [initialIndicators]);

  // BLOKAS 7 Strategy.tsx: HANDLE STRATEGY SELECT FUNCTION
  const handleStrategySelect = async (strategyId: string) => {
    console.log('🎯 [BLOKAS 7 Strategy.tsx] 📥 STRATEGY SELECTION STARTED:', {
      strategyId,
      signalOnlyMode,
      currentStrategy: strategy
    });

    // SUB-BLOKAS 7.1 Strategy.tsx: SIGNAL ONLY MODE CHECK
    if (signalOnlyMode === false && onIndicatorsChange) {
      console.log('🎯 [SUB-BLOKAS 7.1 Strategy.tsx] Setting strategy state to:', strategyId);
      setStrategy(strategyId);

      // SUB-BLOKAS 7.1.1 Strategy.tsx: INITIAL INDICATORS GUARD CHECK
      if (initialIndicators && initialIndicators.length > 0) {
        console.log('🎯 [SUB-BLOKAS 7.1.1 Strategy.tsx] Skipping auto-populate - indicators already exist:', initialIndicators.length);
        return;
      }

      const selectedStrategy = strategies.find(s => s.id === strategyId);
      console.log('🎯 [SUB-BLOKAS 7.1 Strategy.tsx] Found strategy object:', selectedStrategy?.name);

      if (selectedStrategy && onIndicatorsChange) {
        const normalizedKey = toStrategyKey(strategyId) ?? 'trend';
        const indicators = generateIndicatorsForStrategy(normalizedKey);

        console.log('📊 [SUB-BLOKAS 7.1 Strategy.tsx] Generated indicators for strategy:', normalizedKey, '→', indicators);

        onIndicatorsChange(indicators);

        toast.success(`Strategy updated: ${selectedStrategy.name}`);
      } else {
        console.warn('⚠️ [SUB-BLOKAS 7.1 Strategy.tsx] Strategy selection issue:', {
          selectedStrategy: !!selectedStrategy,
          onIndicatorsChange: !!onIndicatorsChange
        });
      }
    } else if (onIndicatorsChange && initialIndicators && initialIndicators.length > 0) {
      // SUB-BLOKAS 7.2 Strategy.tsx: EDIT MODE HANDLING
      console.log('🎯 [SUB-BLOKAS 7.2 Strategy.tsx] Edit mode - setting strategy without changing existing indicators');
      setStrategy(strategyId);

      toast.success(`Strategy updated: ${strategies.find(s => s.id === strategyId)?.name}`);
    } else {
      // SUB-BLOKAS 7.3 Strategy.tsx: SIGNAL ONLY MODE HANDLING
      console.log('🎯 [SUB-BLOKAS 7.3 Strategy.tsx] Strategy selection in signal only mode - setting strategy without auto-configuring indicators');
      setStrategy(strategyId);

      toast.success(`Strategy updated: ${strategies.find(s => s.id === strategyId)?.name}`);
    }
  };

  // BLOKAS 8 Strategy.tsx: MAP INDICATOR NAME TO TYPE FUNCTION
  const mapIndicatorNameToType = (indicatorName: string): string => {
    console.log(`🔧 [BLOKAS 8 Strategy.tsx] Mapping indicator name to type: ${indicatorName}`);
    const name = indicatorName.toLowerCase();

    if (name.includes('ema')) return 'ema';
    if (name.includes('moving average')) return 'sma';
    if (name.includes('macd')) return 'macd';
    if (name.includes('adx')) return 'adx';
    if (name.includes('stochastic')) return 'stochastic';
    if (name.includes('momentum oscillator')) return 'rsi';
    if (name.includes('momentum')) return 'momentum';
    if (name.includes('price action')) return 'price_action';
    if (name.includes('level 2')) return 'level2_data';
    if (name.includes('volume')) return 'volume';
    if (name.includes('rsi')) return 'rsi';
    if (name.includes('fibonacci')) return 'fibonacci';
    if (name.includes('pivot')) return 'pivot_points';
    if (name.includes('candlestick')) return 'candlestick';
    if (name.includes('support/resistance')) return 'support_resistance';
    if (name.includes('trend line')) return 'trend_lines';
    if (name.includes('fibonacci retracement')) return 'fibonacci';
    if (name.includes('momentum indicator')) return 'momentum';
    if (name.includes('volume profile')) return 'volume_profile';
    if (name.includes('order flow')) return 'order_flow';
    if (name.includes('market profile')) return 'market_profile';
    if (name.includes('time and sales')) return 'time_sales';
    
    console.log(`🔧 [BLOKAS 8 Strategy.tsx] Indicator type not found, defaulting to 'sma'`);
    return 'sma';
  };

  // BLOKAS 9 Strategy.tsx: GET DEFAULT SETTINGS FUNCTION
  const getDefaultSettings = (indicatorType: string) => {
    console.log(`🔧 [BLOKAS 9 Strategy.tsx] Getting default settings for indicator type: ${indicatorType}`);
    switch (indicatorType) {
      case 'rsi':
        return { period: 14, overbought: 70, oversold: 30 };
      case 'macd':
        return { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 };
      case 'bollinger':
        return { period: 20, stdDev: 2 };
      case 'stochastic':
        return { kPeriod: 14, dPeriod: 3, smoothPeriod: 3 };
      case 'sma':
      case 'ema':
        return { period: 20 };
      case 'adx':
        return { period: 14 };
      case 'volume':
        return { period: 20 };
      case 'momentum':
        return { period: 14 };
      case 'price_action':
      case 'level2_data':
      case 'fibonacci':
      case 'pivot_points':
      case 'candlestick':
      case 'support_resistance':
      case 'trend_lines':
      case 'volume_profile':
      case 'order_flow':
      case 'market_profile':
      case 'time_sales':
        return {};
      default:
        console.log(`🔧 [BLOKAS 9 Strategy.tsx] Using default settings for unknown indicator type`);
        return { period: 14 };
    }
  };

  // BLOKAS 10 Strategy.tsx: HANDLE SIGNAL MODE TOGGLE FUNCTION
  const handleSignalModeToggle = async () => {
    console.log('🔄 [BLOKAS 10 Strategy.tsx] Signal Only Mode toggle initiated');
    const newMode = !signalOnlyMode;
    console.log('🔄 [BLOKAS 10 Strategy.tsx] Toggling Signal Only Mode:', signalOnlyMode, '→', newMode);

    if (!user?.id || !botId) {
      toast.error('Please save the bot first');
      return;
    }

    setSignalOnlyMode(newMode);

    // SUB-BLOKAS 10.1 Strategy.tsx: DETERMINE NEW INDICATORS BASED ON TOGGLE
    let updatedIndicators: any[] = [];

    if (newMode) {
      // SUB-BLOKAS 10.1.1 Strategy.tsx: TURNING ON SIGNAL ONLY MODE
      updatedIndicators = [];
      console.log('🔄 [SUB-BLOKAS 10.1.1 Strategy.tsx] Signal Only enabled: cleared UI (user will add manually)');
    } else {
      // SUB-BLOKAS 10.1.2 Strategy.tsx: TURNING OFF SIGNAL ONLY MODE
      const hasAny = Array.isArray(initialIndicators) && initialIndicators.length > 0;
      if (!hasAny) {
        const restored = prevStrategyIndicatorsRef.current && prevStrategyIndicatorsRef.current.length > 0
          ? prevStrategyIndicatorsRef.current
          : (() => {
              const key = toStrategyKey(strategy);
              return key ? generateIndicatorsForStrategy(key) : [];
            })();
        updatedIndicators = restored;
        console.log('🔄 [SUB-BLOKAS 10.1.2 Strategy.tsx] Signal Only disabled: restored/generated indicators', updatedIndicators.length);
      } else {
        updatedIndicators = initialIndicators;
      }
      prevStrategyIndicatorsRef.current = null;
    }

    // SUB-BLOKAS 10.2 Strategy.tsx: UPDATE PARENT FORM STATE
    console.log('🔄 [SUB-BLOKAS 10.2 Strategy.tsx] Persisting via callbacks: signalOnly =', newMode, ', indicators count =', Array.isArray(updatedIndicators) ? updatedIndicators.length : 'N/A');

    if (onSignalOnlyChange) {
      onSignalOnlyChange(newMode);
    }

    if (onIndicatorsChange) {
      onIndicatorsChange(updatedIndicators);
    }

    toast.success(newMode
      ? 'Signal Only Mode enabled - add your indicators manually'
      : 'Strategy Mode enabled - indicators restored'
    );
  };

  // BLOKAS 11 Strategy.tsx: GET COMPLEXITY COLOR FUNCTION
  const getComplexityColor = (complexity: string) => {
    console.log(`🔧 [BLOKAS 11 Strategy.tsx] Getting color for complexity level: ${complexity}`);
    switch (complexity) {
      case 'beginner':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'advanced':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        console.log(`🔧 [BLOKAS 11 Strategy.tsx] Using default color for unknown complexity`);
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  // BLOKAS 12 Strategy.tsx: RENDER STRATEGY TOOLTIP FUNCTION
  const renderStrategyTooltip = (strat: StrategyOption) => {
    console.log(`🔧 [BLOKAS 12 Strategy.tsx] Rendering tooltip for strategy: ${strat.name}`);
    return (
      <div className="space-y-4">
        <h4 className="font-semibold mb-3 text-lg">{strat.name}</h4>
        <p className="mb-4 text-gray-300">{strat.tooltip.description}</p>
        
        <div className="space-y-4">
          <div>
            <h5 className="font-medium text-green-400 mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Advantages
            </h5>
            <ul className="list-disc pl-4 space-y-1 text-gray-300">
              {strat.tooltip.pros.map((pro, index) => (
                <li key={index}>{pro}</li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="font-medium text-red-400 mb-2 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Limitations
            </h5>
            <ul className="list-disc pl-4 space-y-1 text-gray-300">
              {strat.tooltip.cons.map((con, index) => (
                <li key={index}>{con}</li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Best For
            </h5>
            <ul className="list-disc pl-4 space-y-1 text-gray-300">
              {strat.tooltip.bestFor.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="font-medium text-purple-400 mb-2 flex items-center gap-2">
              <LineChart className="h-4 w-4" />
              Key Indicators
            </h5>
            <ul className="list-disc pl-4 space-y-1 text-gray-300">
              {strat.tooltip.indicators.map((indicator, index) => (
                <li key={index}>{indicator}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // BLOKAS 13 Strategy.tsx: MAIN RETURN STATEMENT (JSX)
  console.log(`🔧 [BLOKAS 13 Strategy.tsx] Rendering JSX for Strategy component`);
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      {/* SUB-BLOKAS 13.1 Strategy.tsx: MAIN CONTAINER */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Trading Strategy
        </h2>

        {/* SUB-BLOKAS 13.1.1 Strategy.tsx: SIGNAL ONLY MODE SECTION */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              <span className="font-medium text-gray-900 dark:text-white">Signal Only Mode</span>
              <Tooltip
                content={
                  <div className="space-y-4">
                    <h4 className="font-semibold mb-2">Signal Only Mode</h4>
                    <p className="mb-3">
                      In this mode, your bot will only execute trades based on external signals and alerts, ignoring its built-in strategy rules.
                    </p>
                    
                    <div className="space-y-3">
                      <div>
                        <h5 className="font-medium text-yellow-400 mb-1">Compatible Signal Sources:</h5>
                        <ul className="list-disc pl-4 space-y-1 text-gray-300">
                          <li>TradingView alerts</li>
                          <li>Custom webhook signals</li>
                          <li>Third-party signal providers</li>
                          <li>Manual trading signals</li>
                        </ul>
                      </div>

                      <div>
                        <h5 className="font-medium text-green-400 mb-1">Benefits:</h5>
                        <ul className="list-disc pl-4 space-y-1 text-gray-300">
                          <li>Combine multiple signal sources</li>
                          <li>Use external analysis tools</li>
                          <li>Manual trading oversight</li>
                          <li>Flexible strategy integration</li>
                        </ul>
                      </div>

                      <div className="flex items-start gap-2 bg-yellow-900/20 p-2 rounded">
                        <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <p className="text-yellow-300 text-xs">
                          Make sure to properly configure and test your signal sources before enabling this mode.
                        </p>
                      </div>
                    </div>
                  </div>
                }
                position="top"
              >
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <div className="flex items-center gap-3">
              {/* SUB-BLOKAS 13.1.1.1 Strategy.tsx: STRATEGY TOGGLE LABEL */}
              <span className={cn(
                "text-sm font-medium transition-colors",
                !signalOnlyMode 
                  ? "text-green-500 dark:text-green-400" 
                  : "text-gray-400 dark:text-gray-500" 
              )}>
                {!signalOnlyMode ? 'Enabled' : 'Disabled'}
              </span>
              <button
                onClick={handleSignalModeToggle}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                  !signalOnlyMode ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    !signalOnlyMode ? "translate-x-6" : "translate-x-1"
                  )}
                />
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Trade solely based on external signals and alerts
          </p>
        </div>

        {/* SUB-BLOKAS 13.2 Strategy.tsx: STRATEGY SELECTION SECTION */}
        {!signalOnlyMode && (
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Select Strategy
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {strategies.map((strat) => (
                <div
                  key={strat.id}
                  className="relative"
                >
                  <button
                    onClick={() => handleStrategySelect(strat.id)}
                    className={cn(
                      "p-4 rounded-lg border transition-all text-left w-full",
                      "hover:shadow-md",
                      strategy === strat.id
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700"
                    )}
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={cn(
                          "p-2 rounded-lg",
                          strategy === strat.id
                            ? "bg-blue-100 dark:bg-blue-900/30 text-blue-500"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                        )}>
                          {strat.icon}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {strat.name}
                          </h4>
                          <span className={cn(
                            "inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full",
                            getComplexityColor(strat.complexity) 
                          )}>
                            {strat.complexity}
                          </span>
                        </div>
                        <div className="ml-auto">
                          <Tooltip
                            content={renderStrategyTooltip(strat)}
                            position="left"
                          >
                            <Info className="h-4 w-4 text-gray-400 cursor-help" />
                          </Tooltip>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {strat.description}
                      </p>
                    </div> 
                  </button>
                </div>
              ))}
            </div>
            
            {/* SUB-BLOKAS 13.2.1 Strategy.tsx: NO STRATEGY SELECTED MESSAGE */}
            {!strategy && (
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800/30">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-1">
                      No Strategy Selected
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Please select a trading strategy above to automatically configure indicators, or manage indicators manually in the Primary Time section.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}; 

export default Strategy;